import { getBrandingBackgroundImage } from '@sonifi/wb-branding/services/branding.service';
import { LOADING_STATUS } from '@sonifi/wb-core/common/Constants';
import apiService from '@sonifi/wb-core/services/api.service';
import { ENDPOINTS } from '@sonifi/wb-core/services/endpointsConstants';
import { parseQueryParams } from '@sonifi/wb-core/utils/parseHelper';
import { MEDIA_KEYS } from '@sonifi/wb-media/constants/WbMediaConstants';
import { ALERTS_IDS } from '@sonifi/wb-notifier/constants/notifierConstants';
import { initConfirm } from '@sonifi/wb-notifier/services/confirm.service';
import { setAlert } from '@sonifi/wb-notifier/store/notifierSlice';
import { PRODUCTS_KEYS } from '@sonifi/wb-products/constants/wbProductsConstants';
import {
  getProductsSettings,
  getSettingByProduct,
} from '@sonifi/wb-products/services/products.service';
import { findRegionPreset } from '@sonifi/wb-screenbuilder/services/regions.service';
import {
  addNodeToWbTree,
  addWarnings,
  buildWbTreeByNode,
  findNodeById,
  findNodeByUri,
  findNodeParent,
  isNodeRoot,
  removeNodeFromWbTree,
} from '@sonifi/wb-tree/utils/helper';
import escape from 'lodash/escape';
import {
  checkUnpublishedChanges,
  setCompendiaMenu,
  setHotelDirectortyMenu,
  setLoadingHotelDirectory,
  setDeepLinkConfig,
  setNodePreviewSelected,
  setPublishingStatus,
  setScreenFromNode,
  updateHDMediadependencies,
  updateHDTree,
} from '../store/hotelDirectorySlice';
import {
  getJobsPublishStatus,
  processLatestPublishJob,
} from './hdJobs.service';
import {
  buildMediaDataForMediaDependencies,
  buildMediaParam,
  createHotelDirectoryTree,
  updateHDTreeByMediaDependencies,
} from './helpers/hdHelper';
import { verifyCompendiaStructure } from './helpers/hdNodeValidations';
import { getBodyCompendia } from './helpers/hdRequestHelper';

export const getMediaDependencies = async ({
  groupUri,
  mediaData,
  mediaKeys,
}) => {
  try {
    const resMedia = [];
    const promises = [];

    for (const mediaKey of mediaKeys) {
      const params = {
        group: groupUri,
        id__in: buildMediaParam(mediaData, mediaKey),
        rpp: -1,
      };
      const url = `${ENDPOINTS[mediaKey]}?${parseQueryParams(params)}`;
      promises.push(apiService.get(url));
    }

    const responses = await Promise.all(promises);
    responses.forEach((res, index) => {
      const mediaMapped = res.data.objects.map((resMedia) => ({
        ...resMedia,
        files:
          mediaKeys[index] !== 'SERVICES' && mediaKeys[index] !== MEDIA_KEYS.TYPE.QRCODES.KEY.toUpperCase()
            ? resMedia.files.map((file) => ({
                ...res.data._embedded[file],
              }))
            : undefined,
        thumbnail_pattern:
          mediaKeys[index] === 'VIDEOS'
            ? resMedia.thumbnail_pattern
                .replace('{WIDTH}', 0)
                .replace('{HEIGHT}', 0)
            : undefined,
      }));

      resMedia.push(...mediaMapped);
    });

    return resMedia;
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const getCompendiaMenu = async ({
  currentMediaDependencies,
  groupUri,
  selectedLanguage,
}) => {
  try {
    const url = `${ENDPOINTS.COMPENDIA}?group=${groupUri}&languages=${selectedLanguage}&allow_disabled=true`;
    const res = await apiService.get(url);
    const mediaDependencies =
      currentMediaDependencies ||
      (await getMediaDependencies({
        groupUri,
        mediaData: res.data._embedded,
        mediaKeys: [
          MEDIA_KEYS.TYPE.IMAGES.KEY.toUpperCase(),
          MEDIA_KEYS.TYPE.VIDEOS.KEY.toUpperCase(),
          MEDIA_KEYS.TYPE.SERVICES.KEY.toUpperCase(),
          MEDIA_KEYS.TYPE.QRCODES.KEY.toUpperCase(),
        ],
      }));

    const { contents, ...rest } = res.data.objects[0];
    const tree = createHotelDirectoryTree({
      menu: contents,
      embedded: res.data._embedded,
      mediaDependencies,
    });

    const compendiaMenu = {
      ...rest,
      menu: {
        ...tree,
        isRoot: true,
      },
    };
    return { compendiaMenu, mediaDependencies };
  } catch (error) {
    return Promise.reject(error);
  }
};

export const initHotelDirectory =
  ({ groupId, id, platform }) =>
  async (dispatch, getState) => {
    dispatch(setLoadingHotelDirectory(LOADING_STATUS.loading));

    try {
      const {
        groups: { selectedGroup },
        hotelDirectory: { selectedLanguage },
      } = getState();

      const { compendiaMenu, mediaDependencies } = await getCompendiaMenu({
        groupUri: selectedGroup.uri,
        selectedLanguage,
      });

      const productsSettings = await dispatch(getProductsSettings(groupId));
      const hdProductSettings = dispatch(
        getSettingByProduct({
          groupId,
          productKey: PRODUCTS_KEYS.HOTEL_DIRECTORY,
        }),
      );
      const background = dispatch(
        getBrandingBackgroundImage({ groupId, productsSettings }),
      );

      const {
        latestPublishJob,
        lastPublishJob,
        lastPublished,
        lastPublishUser,
      } = await dispatch(
        getJobsPublishStatus({ compendiaMenuUri: compendiaMenu.uri }),
      );

      const node = id
        ? findNodeById({ menu: compendiaMenu.menu, nodeId: parseInt(id) })
        : compendiaMenu.menu;

      await Promise.all([
        dispatch(
          setHotelDirectortyMenu({
            background,
            lastPublished,
            lastPublishUser,
            lastPublishJob,
            latestPublishJob,
            mediaDependencies,
            compendiaMenu,
            platform,
            productSettings: hdProductSettings,
          }),
        ),
        dispatch(setNodePreviewSelected({ node })),
        dispatch(canPublishMenu({ superStrict: false })),
        dispatch(setLoadingHotelDirectory(LOADING_STATUS.success)),
      ]);
      return node.id;
    } catch (error) {
      console.error(error);
      dispatch(setLoadingHotelDirectory(LOADING_STATUS.error));
    }
  };

export const addNodeScreen =
  ({ title, preset }) =>
  async (dispatch, getState) => {
    try {
      const {
        groups: { selectedGroup },
        hotelDirectory: { compendiaMenu, screenFromNode },
      } = getState();

      const body = {
        title: title || 'New Screen',
        group: selectedGroup.uri,
        regions: findRegionPreset(preset),
      };

      const res = await apiService.post(ENDPOINTS.SCREENS, body);

      const node = {
        active: false,
        id: res.data.id,
        screen: res.data,
        subject: res.data,
        hasDeletedContent: false,
        indicatorImage: {
          showIcon: false,
          showTooltip: false,
        },
        secondaryValue: '',
      };

      const bodyComp = getBodyCompendia({
        compendiaMenu,
        newScreen: { active: false, screen: res.data.uri },
        screenFromNode,
      });

      const resMenu = await apiService.patch(
        `${ENDPOINTS.COMPENDIA}/${compendiaMenu.id}`,
        bodyComp,
      );

      dispatch(setScreenFromNode({ fromNode: null })); // Clean Modal add
      const compendiaMenuUpdated = addNodeToWbTree({
        wbTree: compendiaMenu,
        node,
        addNodeFrom: screenFromNode,
      });
      await Promise.all([
        dispatch(
          setCompendiaMenu({
            compendiaMenu: {
              ...compendiaMenuUpdated,
              last_updated: resMenu.data.last_updated,
            },
          }),
        ),
        dispatch(setNodePreviewSelected({ node })),
        dispatch(checkUnpublishedChanges()),
        dispatch(setLoadingHotelDirectory(LOADING_STATUS.success)),
      ]);
      return node;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

export const removeNode = (node) => async (dispatch, getState) => {
  try {
    const {
      hotelDirectory: { compendiaMenu },
    } = getState();

    if (
      compendiaMenu?.menu &&
      isNodeRoot({ menu: compendiaMenu.menu, nodeId: node.id })
    ) {
      dispatch(
        setAlert({
          content: 'The first screen of your directory cannot be deleted.',
          type: 'warning',
          show: true,
          dismissable: true,
        }),
      );
      return null;
    }

    const bodyComp = getBodyCompendia({
      compendiaMenu,
      nodeRemoveId: node.id,
    });
    const resMenu = await apiService.patch(
      `${ENDPOINTS.COMPENDIA}/${compendiaMenu.id}`,
      bodyComp,
    );

    if (!node.subject.isZombie) {
      await apiService.delete(`${ENDPOINTS.SCREENS}/${node.screen.id}`);
    }

    const removeNodeFrom = findNodeParent({ menu: compendiaMenu.menu, node });
    const compendiaMenuUpdated = removeNodeFromWbTree({
      wbTree: compendiaMenu,
      node,
      removeNodeFrom,
    });
    const removeNodeFromUpdated = findNodeById({
      menu: compendiaMenuUpdated.menu,
      nodeId: removeNodeFrom.id,
    });

    await Promise.all([
      dispatch(
        setCompendiaMenu({
          compendiaMenu: {
            ...compendiaMenuUpdated,
            last_updated: resMenu.data.last_updated,
          },
        }),
      ),
      dispatch(setNodePreviewSelected({ node: removeNodeFromUpdated })),
      dispatch(checkUnpublishedChanges()),
      dispatch(setLoadingHotelDirectory(LOADING_STATUS.success)),
    ]);
    return removeNodeFrom;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const updateTreeAfterDeleteContent =
  () => async (dispatch, getState) => {
    const {
      groups: { selectedGroup },
      hotelDirectory: { compendiaMenu, screen },
    } = getState();

    const mediaData = buildMediaDataForMediaDependencies({
      menu: compendiaMenu.menu,
    });

    const mediaDependencies = await getMediaDependencies({
      groupUri: selectedGroup.uri,
      mediaData,
      mediaKeys: [
        MEDIA_KEYS.TYPE.IMAGES.KEY.toUpperCase(),
        MEDIA_KEYS.TYPE.VIDEOS.KEY.toUpperCase(),
        MEDIA_KEYS.TYPE.SERVICES.KEY.toUpperCase(),
        MEDIA_KEYS.TYPE.QRCODES.KEY.toUpperCase(),
      ],
    });

    const menuUpdate = updateHDTreeByMediaDependencies({
      menu: JSON.parse(JSON.stringify(compendiaMenu.menu)),
      mediaDependencies,
    });

    await Promise.all([
      dispatch(
        updateHDTree({
          compendiaMenu: { ...compendiaMenu, menu: menuUpdate },
          screen,
          cleanRegion: false,
        }),
      ),
      dispatch(updateHDMediadependencies({ mediaDependencies })),
    ]);
  };

export const patchHDTree = (compendiaMenu) => async (dispatch, getState) => {
  try {
    const {
      hotelDirectory: { nodePreviewSelected },
    } = getState();

    const bodyComp = getBodyCompendia({
      compendiaMenu,
    });
    const resMenu = await apiService.patch(
      `${ENDPOINTS.COMPENDIA}/${compendiaMenu.id}`,
      bodyComp,
    );

    const nodePreviewUpdated = findNodeByUri({
      menu: compendiaMenu.menu,
      nodeUri: nodePreviewSelected.subject.uri,
    });

    await Promise.all([
      dispatch(
        setCompendiaMenu({
          compendiaMenu: {
            ...compendiaMenu,
            last_updated: resMenu.data.last_updated,
          },
        }),
      ),
      dispatch(setNodePreviewSelected({ node: nodePreviewUpdated })),
      dispatch(checkUnpublishedChanges()),
    ]);
  } catch (error) {
    console.error(error);
    const {
      hotelDirectory: { compendiaMenu: compendiaMenuOld },
    } = getState();
    dispatch(
      setAlert({
        title: 'Settings save failed',
        content: 'The settings for this property could not be saved.',
        type: 'danger',
        show: true,
        dismissable: true,
      }),
    );
    // Restore previous state
    dispatch(
      setCompendiaMenu({
        compendiaMenu: { ...compendiaMenuOld },
      }),
    );
  }
};

export const toggleNodeActive = (node) => async (dispatch, getState) => {
  const {
    hotelDirectory: { compendiaMenu, screen },
  } = getState();

  try {
    const compendiaMenuUpdated = buildWbTreeByNode({
      wbTree: compendiaMenu,
      node,
    });

    const bodyComp = getBodyCompendia({
      compendiaMenu: compendiaMenuUpdated,
    });

    const resMenu = await apiService.patch(
      `${ENDPOINTS.COMPENDIA}/${compendiaMenu.id}`,
      bodyComp,
    );
    await Promise.all([
      dispatch(
        updateHDTree({
          compendiaMenu: {
            ...compendiaMenuUpdated,
            last_updated: resMenu.data.last_updated,
          },
          screen,
        }),
      ),
      dispatch(checkUnpublishedChanges()),
    ]);
  } catch (error) {
    console.error(error);
  }
};

const canPublishMenu =
  ({ superStrict }) =>
  (dispatch) => {
    const validationResults = dispatch(
      verifyCompendiaStructure({ superStrict }),
    );

    let canPublish = true;
    if (validationResults.errors.length) {
      let message =
        'The selected menu cannot be published until the following issues are fixed.<ul>';

      for (const msg of validationResults.errors) {
        message += `<li>${escape(msg)}</li>`;
      }
      message += '</ul>';

      message += addWarnings(validationResults);
      dispatch(
        setAlert({
          content: message,
          type: 'danger',
          show: true,
          dismissable: true,
        }),
      );
      canPublish = false;
    }

    return {
      canPublish,
      validationResults,
    };
  };

export const publishHotelDirectory = () => async (dispatch, getState) => {
  try {
    const {
      hotelDirectory: { compendiaMenu, platform },
    } = getState();

    const { canPublish, validationResults } = dispatch(
      canPublishMenu({ superStrict: true }),
    );

    if (canPublish) {
      const title = 'Are you sure you want to publish the selected directory?';
      const content = addWarnings(validationResults);

      const isConfirmed = await dispatch(
        initConfirm({
          title,
          content,
        }),
      );
      if (!isConfirmed) {
        return false;
      }

      const body = {
        arguments: { platform },
        targets: [compendiaMenu.uri],
        type: 'PUBLISH_COMPENDIUM',
      };

      const res = await apiService.post(ENDPOINTS.JOBS, body);
      dispatch(setPublishingStatus({ job: res.data }));
      await dispatch(
        processLatestPublishJob({
          job: res.data,
          compendiaMenuUri: compendiaMenu.uri,
        }),
      );
    }
  } catch (error) {}
};

export const patchCmsId = (node) => async (dispatch, getState) => {
  const {
    hotelDirectory: { compendiaMenu },
  } = getState();

  try {
    const compendiaMenuUpdated = buildWbTreeByNode({
      wbTree: compendiaMenu,
      node,
    });

    const bodyComp = getBodyCompendia({
      compendiaMenu: compendiaMenuUpdated,
    });

    const resMenu = await apiService.patch(
      `${ENDPOINTS.COMPENDIA}/${compendiaMenu.id}`,
      bodyComp,
    );

    await Promise.all([
      dispatch(
        updateHDTree({
          compendiaMenu: {
            ...compendiaMenuUpdated,
            last_updated: resMenu.data.last_updated,
          },
        }),
      ),
      dispatch(checkUnpublishedChanges()),
      dispatch(setDeepLinkConfig({ showModal: false, item: null })),
    ]);
  } catch (error) {
    console.error(error);
    if (error && Array.isArray(error)) {
      const errors = error.map((err) => `<li>${err.error}</li>`);
      dispatch(
        setAlert({
          id: ALERTS_IDS.hdModal,
          content: `The settings for this property could not be saved. Error(s) returned by server: <ul>${errors}</ul>`,
          type: 'danger',
          show: true,
          dismissable: true,
        }),
      );
    } else {
      dispatch(
        setAlert({
          id: ALERTS_IDS.hdModal,
          content: 'There was an error',
          type: 'danger',
          show: true,
          dismissable: true,
        }),
      );
    }
  }
};

export const exportCompendiaCsv = async ({ group, languages = [] }) => {
  try {
    const langParam = languages.length === 1 ? languages[0] : languages.join(',');

    const url = `${ENDPOINTS.GROUPS}/${group.id}/compendia.csv?languages=${langParam}`;
    const res = await apiService.get(url, {
      headers: {
        Accept: 'text/csv',
      }
    });

    const blob = new Blob([res.data], { type: 'text/csv' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `compendia_${group.name.toLowerCase()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return true;
  } catch (error) {
    console.error('Error exporting compendia CSV:', error);
    return Promise.reject(error);
  }
};

export const importCompendiaCsv = async ({ compendiaId, languages = [], file }) => {
  try {
    const langParam = languages.length === 1 ? languages[0] : languages.join(',');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('languages', langParam);

    const url = `${ENDPOINTS.COMPENDIA}/${compendiaId}`;

    const res = await apiService.patch(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return res.data;
  } catch (error) {
    console.error('Error importing compendia CSV:', error);
    return Promise.reject(error);
  }
};
